import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const TalentDetailedReports = ({ selectedTalent, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate chart loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [selectedTalent]);

  if (!selectedTalent || !selectedTalent.reports) {
    return null;
  }

  // Prepare data for charts
  const chartData = selectedTalent.reports
    .map((report, index) => ({
      reportNumber: `Report ${index + 1}`,
      date: new Date(report.created_at).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }),
      jobSatisfaction: report.job_satisfaction,
      roleClarity: report.role_clarity,
      supportAndSupervision: report.support_and_supervision,
      belongingAndRecognition: report.belonging_and_recognition,
      advocacy: report.advocacy,
      upstartExperience: report.upstart_experience,
    }))
    .reverse(); // Show oldest to newest

  // (no trend calculations currently used)

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
            {selectedTalent.full_name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedTalent.full_name}
            </h2>
            <p className="text-sm text-gray-600">
              {selectedTalent.reports.length} Performance Reports
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Line Chart */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Performance Trends Over Time
        </h3>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                <p className="text-gray-600 text-sm font-medium">
                  Loading Chart...
                </p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke="#64748b"
                  fontSize={12}
                  domain={[0, 100]}
                  tickCount={6}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                  labelStyle={{ color: "#374151", fontWeight: "bold" }}
                  labelFormatter={(label) => `Date: ${label}`}
                  formatter={(value, name) => [
                    value,
                    name
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase()),
                  ]}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="jobSatisfaction"
                  stroke="#3B82F6"
                  strokeWidth={3}
                  dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                  name="Job Satisfaction"
                />
                <Line
                  type="monotone"
                  dataKey="roleClarity"
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                  name="Role Clarity"
                />
                <Line
                  type="monotone"
                  dataKey="supportAndSupervision"
                  stroke="#F59E0B"
                  strokeWidth={3}
                  dot={{ fill: "#F59E0B", strokeWidth: 2, r: 4 }}
                  name="Support & Supervision"
                />
                <Line
                  type="monotone"
                  dataKey="belongingAndRecognition"
                  stroke="#8B5CF6"
                  strokeWidth={3}
                  dot={{ fill: "#8B5CF6", strokeWidth: 2, r: 4 }}
                  name="Belonging & Recognition"
                />
                <Line
                  type="monotone"
                  dataKey="advocacy"
                  stroke="#EF4444"
                  strokeWidth={3}
                  dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                  name="Advocacy"
                />
                <Line
                  type="monotone"
                  dataKey="upstartExperience"
                  stroke="#06B6D4"
                  strokeWidth={3}
                  dot={{ fill: "#06B6D4", strokeWidth: 2, r: 4 }}
                  name="Upstart Experience"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default TalentDetailedReports;
